import { useState, useEffect } from "react";
import { Lock, ShieldCheck, Sparkles } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

export default function AgeGate({ children }) {
  const { t, lang, setLang } = useLang();
  const [verified, setVerified] = useState(() => localStorage.getItem("velvet_age_verified") === "true");

  useEffect(() => {
    document.body.style.overflow = verified ? "" : "hidden";
  }, [verified]);

  if (verified) return children;

  return (
    <>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-xl" data-testid="age-gate-overlay">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 glow-rose blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 glow-rose blur-3xl" />
        <div className="relative w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-950/90 p-8 sm:p-10 text-center animate-fade-up noise-overlay">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-rose-800/50 bg-rose-950/40">
            <Lock className="h-7 w-7 text-rose-400" />
          </div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-rose-400 mb-3">{t("age.brand")}</div>
          <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-zinc-100 mb-4">{t("age.title")}</h1>
          <p className="text-sm text-zinc-400 leading-relaxed mb-8">{t("age.body")}</p>
          <div className="space-y-3">
            <button
              data-testid="age-verification-yes-button"
              onClick={() => { localStorage.setItem("velvet_age_verified", "true"); setVerified(true); }}
              className="w-full rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-medium px-5 py-3.5 shadow-lg shadow-rose-950/50 transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              {t("age.yes")}
            </button>
            <button
              data-testid="age-verification-no-button"
              onClick={() => { window.location.href = "https://www.google.com"; }}
              className="w-full rounded-lg bg-zinc-900 hover:bg-zinc-800 text-zinc-400 border border-zinc-800 px-5 py-3.5 transition-all text-sm"
            >
              {t("age.no")}
            </button>
          </div>
          <div className="mt-8 flex items-center justify-center gap-6 text-[11px] text-zinc-600">
            <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> {t("age.anon")}</span>
            <span className="flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> {t("age.discreet")}</span>
          </div>
          <div className="mt-6 flex items-center justify-center gap-2 text-xs">
            <button onClick={() => setLang("nl")} className={lang === "nl" ? "text-rose-400 font-semibold" : "text-zinc-500 hover:text-zinc-300"}>NL</button>
            <span className="text-zinc-700">·</span>
            <button onClick={() => setLang("en")} className={lang === "en" ? "text-rose-400 font-semibold" : "text-zinc-500 hover:text-zinc-300"}>EN</button>
          </div>
        </div>
      </div>
      <div className="pointer-events-none blur-sm select-none">{children}</div>
    </>
  );
}
